import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { CardComp, TableCompData } from "../../../../components";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import { OpportunityStatusBadge } from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import InvestorPortalPageHeader, {
  InvestorPortalPrimaryButton,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import InvestorPortalTableSection, {
  TableRowLink,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import { openStaffPublicPreview } from "../../../../utils/investorAdminPreviewAdapter";
import { OPPORTUNITY_STATUS } from "../../../../store/admin/investorPortal/constants";
import { isOpportunityEditable } from "../../../../store/admin/investorPortal/opportunityLifecycle";
import { formatMoney } from "../../../../utils/currencyFormat";
import {
  useListStaffOpportunitiesQuery,
  useStaffOpportunityLifecycleMutation,
} from "../../../../store/admin/investorPortal/staffInvestmentsApi";
import { mapStaffOpportunityListItem } from "../../../../utils/staffInvestmentsAdapter";
import moment from "moment";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

const PER_PAGE = 10;

const LIFECYCLE_ACTIONS = {
  [OPPORTUNITY_STATUS.INTERNAL_REVIEW]: "submit-review",
  [OPPORTUNITY_STATUS.PUBLISHED]: "publish",
  [OPPORTUNITY_STATUS.DRAFT]: "unpublish",
  [OPPORTUNITY_STATUS.ARCHIVED]: "archive",
};

function OpportunitiesListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [riskFilter, setRiskFilter] = useState("");
  const [closingSoon, setClosingSoon] = useState(false);
  const [fullyFunded, setFullyFunded] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [lifecycleAction, { isLoading: isLifecycleLoading }] = useStaffOpportunityLifecycleMutation();

  const apiQuery = useListStaffOpportunitiesQuery({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
    status: statusFilter || (fullyFunded ? OPPORTUNITY_STATUS.FULLY_FUNDED : undefined),
    category: categoryFilter || undefined,
    risk_level: riskFilter ? riskFilter.toLowerCase() : undefined,
    closing_soon: closingSoon ? 1 : undefined,
  });

  const all = useMemo(
    () => (apiQuery.data?.items || []).map(mapStaffOpportunityListItem).filter(Boolean),
    [apiQuery.data]
  );

  const filtered = useMemo(() => {
    if (fullyFunded && !statusFilter) {
      return all.filter((o) => o.status === OPPORTUNITY_STATUS.FULLY_FUNDED);
    }
    return all;
  }, [all, fullyFunded, statusFilter]);

  const apiPagination = apiQuery.data?.pagination || {};
  const pagination = {
    currentPage: apiPagination.current_page || page,
    totalPages: apiPagination.last_page || 1,
    total: apiPagination.total || filtered.length,
    perPage: apiPagination.per_page || PER_PAGE,
    onPageChange: setPage,
  };

  const categories = [...new Set(all.map((o) => o.category).filter(Boolean))];
  const riskLevels = [...new Set(all.map((o) => o.riskLevel).filter(Boolean))];

  const hasFilters = Boolean(
    search || statusFilter || categoryFilter || riskFilter || closingSoon || fullyFunded
  );

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setCategoryFilter("");
    setRiskFilter("");
    setClosingSoon(false);
    setFullyFunded(false);
    setPage(1);
  };

  const emptyState = apiQuery.isError ? (
    <InvestorPortalTableEmpty
      variant="error"
      title="We couldn’t load investment opportunities."
      onCta={() => apiQuery.refetch()}
    />
  ) : hasFilters ? (
    <InvestorPortalTableEmpty
      variant="filtered"
      title="No opportunities match your filters."
      onCta={clearFilters}
    />
  ) : (
    <InvestorPortalTableEmpty
      variant="empty"
      title="No investment opportunities yet."
      description="Create your first opportunity to start publishing investment products."
      ctaLabel="Create Opportunity"
      onCta={() => navigate("/super_admin/investor_portal/investments/opportunities/new")}
    />
  );

  const handleStatusAction = (opp, status, label) => {
    const isArchive = status === OPPORTUNITY_STATUS.ARCHIVED;
    setConfirm({
      title: label,
      message: isArchive
        ? `Archive "${opp.title}"? It will be removed from the investor marketplace.`
        : `Are you sure you want to ${label.toLowerCase()} "${opp.title}"?`,
      confirmText: isArchive ? "Yes, archive" : "Yes, continue",
      confirmButtonClass: isArchive ? "bg-danger text-white" : "bg-primary1 text-white",
      action: async () => {
        const action = LIFECYCLE_ACTIONS[status];
        if (!action) {
          toast.error("This action is not available via API yet.");
          setConfirm(null);
          return;
        }
        try {
          await lifecycleAction({ id: opp.id, action }).unwrap();
          toast.success(`${opp.title} — ${label}`);
        } catch (error) {
          toast.error(error?.data?.message || `Unable to ${label.toLowerCase()}.`);
        }
        setConfirm(null);
      },
    });
  };

  const columns = [
    {
      name: "Opportunity",
      selector: (row) => row.title,
      cell: (row) => (
        <TableRowLink onClick={() => navigate(`/super_admin/investor_portal/investments/opportunities/${row.id}`)}>
          {row.title}
        </TableRowLink>
      ),
      minWidth: "200px",
      grow: 2,
    },
    { name: "Category", selector: (row) => row.category, minWidth: "110px" },
    {
      name: "Status",
      cell: (row) => <OpportunityStatusBadge status={row.status} />,
      minWidth: "120px",
    },
    {
      name: "Unit price",
      selector: (row) => formatMoney(row.unitPriceNgn),
      minWidth: "130px",
      right: true,
    },
    {
      name: "Target",
      selector: (row) => formatMoney(row.fundingTargetNgn),
      minWidth: "140px",
      right: true,
    },
    {
      name: "Funded",
      selector: (row) => formatMoney(row.fundedAmountNgn),
      minWidth: "140px",
      right: true,
    },
    {
      name: "Progress",
      cell: (row) => {
        const pct = row.fundingTargetNgn ? Math.round((row.fundedAmountNgn / row.fundingTargetNgn) * 100) : 0;
        return <span className="fw-semibold">{pct}%</span>;
      },
      minWidth: "90px",
      center: true,
    },
    {
      name: "Participants",
      selector: (row) => row.participantCount ?? 0,
      minWidth: "110px",
      center: true,
    },
    {
      name: "Closing",
      selector: (row) => (row.fundingCloseDate ? moment(row.fundingCloseDate).format("DD-MMM-YY") : "—"),
      minWidth: "100px",
    },
    {
      name: "Maturity",
      selector: (row) => (row.maturityDate ? moment(row.maturityDate).format("DD-MMM-YY") : "—"),
      minWidth: "100px",
    },
    {
      name: "Actions",
      cell: (row) => {
        const items = [
          { name: "View", action: () => navigate(`/super_admin/investor_portal/investments/opportunities/${row.id}`) },
        ];
        if (isOpportunityEditable(row.status)) {
          items.push({
            name: "Edit",
            action: () =>
              navigate(`/super_admin/investor_portal/investments/opportunities/${row.id}/edit`),
          });
        }
        items.push({
          name: "Preview public page",
          action: () => openStaffPublicPreview(row.slug || row.id),
        });
        if ([OPPORTUNITY_STATUS.DRAFT, OPPORTUNITY_STATUS.INTERNAL_REVIEW, OPPORTUNITY_STATUS.SCHEDULED].includes(row.status)) {
          items.push({ name: "Publish", action: () => handleStatusAction(row, OPPORTUNITY_STATUS.PUBLISHED, "Publish") });
        }
        if (row.status === OPPORTUNITY_STATUS.PUBLISHED) {
          items.push({ name: "Pause", action: () => handleStatusAction(row, OPPORTUNITY_STATUS.DRAFT, "Pause") });
        }
        if (row.status !== OPPORTUNITY_STATUS.ARCHIVED) {
          items.push({ name: "Archive", action: () => handleStatusAction(row, OPPORTUNITY_STATUS.ARCHIVED, "Archive") });
        }
        return <TableActionDropdown items={items} row={row} />;
      },
      width: "80px",
      center: true,
    },
  ];

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader
        title="Investment Opportunities"
        action={
          <div data-tour="staff-invest-opps-create">
          <InvestorPortalPrimaryButton
            btnText="Create Opportunity"
            onClick={() => navigate("/super_admin/investor_portal/investments/opportunities/new")}
          />
          </div>
        }
      />

      <CardComp cardClassName="border-0 shadow-sm mb-3 investor-portal-filter-bar">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-lg-4">
            <input
              className="form-control"
              placeholder="Search opportunities..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="col-6 col-lg-2">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              {Object.values(OPPORTUNITY_STATUS).map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-lg-2">
            <select
              className="form-select"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-lg-2">
            <select
              className="form-select"
              value={riskFilter}
              onChange={(e) => {
                setRiskFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All risk levels</option>
              {riskLevels.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-lg-2 d-flex flex-wrap gap-3 pb-1">
            <label className="small mb-0 d-flex align-items-center gap-1">
              <input
                type="checkbox"
                checked={closingSoon}
                onChange={(e) => {
                  setClosingSoon(e.target.checked);
                  setPage(1);
                }}
              />
              Closing soon
            </label>
            <label className="small mb-0 d-flex align-items-center gap-1">
              <input
                type="checkbox"
                checked={fullyFunded}
                onChange={(e) => {
                  setFullyFunded(e.target.checked);
                  setPage(1);
                }}
              />
              Fully funded
            </label>
          </div>
        </div>
      </CardComp>

      <InvestorPortalTableSection pagination={pagination} data-tour="staff-invest-opps-list">
        <TableCompData
          columns={columns}
          data={filtered}
          progressPending={apiQuery.isFetching || isLifecycleLoading}
          progressComponent={<TableSkeletonLoader rows={8} columns={11} />}
          noDataComponent={emptyState}
        />
      </InvestorPortalTableSection>

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.action}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText || "Yes, continue"}
        cancelText="Cancel"
        confirmButtonClass={confirm?.confirmButtonClass || "bg-primary1 text-white"}
      />
    </div>
  );
}

export default withInvestorPortalGuard(OpportunitiesListPage, "investment");
